import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { PortMap, PointPort, EdgePort } from '../../../port';
import { computeAngleCenter } from '../../../port';
import type { SolvedTrapezoid } from '../../../analytics';
import type { PlacedShape } from '../../../placement';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { resolveGeometryStyle } from '../../coloring';
import {
  initShapeCreatorContext,
  HIDDEN_POINT_ATTRS,
  createBorderStyles,
  createGeneralAngleMarkers,
  applyEdgeVisibilityOverrides,
  QUAD_ANGLE_KEYS,
  QUAD_VERTEX_NAMES,
  resolveBaseVertexStyle,
  buildVertexPerIndex,
} from '../shape-creator.util';
import type { TrapezoidElement } from './trapezoid.model';
import { TRAPEZOID_EDGE_NAMES, TRAPEZOID_PROPERTY_FOR_EDGE } from './trapezoid.model';

/**
 * Create a trapezoid element with placed dependent points.
 *
 * A and B are control points (define the base/bottom edge).
 * D is placed from the solved trapezoid (left side at angle).
 * C is placed at its solved position (top-right vertex).
 * Only top and bottom sides are parallel; the non-parallel sides
 * have independent lengths, so C cannot be derived from D like
 * in a parallelogram.
 */
export function createTrapezoid(
  element: TrapezoidElement & { id: string },
  placed: PlacedShape,
  solved: SolvedTrapezoid,
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const ctx = initShapeCreatorContext(element, context);
  const { elementFactory, elementDisplay, board, jsxObjects, labels, styleCtx } = ctx;
  const givenKeys = new Set(Object.keys(element.params));

  const vA = placed.vertices['A'] as [number, number];
  const vB = placed.vertices['B'] as [number, number];
  const vC = placed.vertices['C'] as [number, number];
  const vD = placed.vertices['D'] as [number, number];

  // --- Create point system ---
  // A, B: control points (base edge)
  // D: placed from solved (left-side vertex at angle from A)
  // C: placed at solved position (top-right vertex; NOT derived from D like parallelogram)

  const pA = board.create('point', vA, { ...HIDDEN_POINT_ATTRS, name: 'A' }) as JXGElement;
  const pB = board.create('point', vB, { ...HIDDEN_POINT_ATTRS, name: 'B' }) as JXGElement;
  const pD = board.create('point', vD, { ...HIDDEN_POINT_ATTRS, name: 'D' }) as JXGElement;
  const pC = board.create('point', vC, { ...HIDDEN_POINT_ATTRS, name: 'C' }) as JXGElement;

  const tA = pA as unknown as { X: () => number; Y: () => number };
  const tB = pB as unknown as { X: () => number; Y: () => number };
  const tC = pC as unknown as { X: () => number; Y: () => number };
  const tD = pD as unknown as { X: () => number; Y: () => number };

  // --- Build border styles using resolved cascade ---
  const borderPerEdge = createBorderStyles(
    TRAPEZOID_EDGE_NAMES,
    TRAPEZOID_PROPERTY_FOR_EDGE,
    'Trapezoid',
    element,
    givenKeys,
    ctx,
  );

  // --- Create polygon ---
  const polyStyle = resolveGeometryStyle('polygon', 'Trapezoid', styleCtx, elementDisplay);
  const borderStyle = resolveGeometryStyle('line', 'Trapezoid', styleCtx, elementDisplay);
  const polygon = elementFactory.polygon({
    vertices: [pA, pB, pC, pD],
    name: element.id,
    vertexNames: ['A', 'B', 'C', 'D'],
    style: {
      fillColor: polyStyle.fillColor,
      fillOpacity: polyStyle.fillOpacity,
      strokeWidth: 0,
      vertex: resolveBaseVertexStyle('Trapezoid', ctx),
      vertexPerIndex: buildVertexPerIndex(QUAD_VERTEX_NAMES, 'Trapezoid', ctx),
      border: { strokeWidth: borderStyle.strokeWidth ?? 2, color: borderStyle.color },
      borderPerEdge,
    },
  });

  jsxObjects.push(polygon.polygon, ...polygon.vertices, ...polygon.borders);
  jsxObjects.push(...polygon.helpers);
  labels.push(...polygon.borderLabels);

  // --- Create angle markers (always created) ---
  // Trapezoid: all 4 angles can be unique, show all.
  const angleElements = createGeneralAngleMarkers(polygon.vertices, 'Trapezoid', element, givenKeys, ctx, 4);

  // --- Apply per-edge visibility overrides ---
  applyEdgeVisibilityOverrides(polygon.borders, TRAPEZOID_EDGE_NAMES, elementDisplay);

  const ports = buildTrapezoidPorts(tA, tB, tC, tD);

  // Named objects for PATCH_DISPLAY targeting
  const namedObjects: Record<string, import('jsxgraph').GeometryElement> = {};
  for (let i = 0; i < TRAPEZOID_EDGE_NAMES.length; i++) {
    namedObjects[`edge.${TRAPEZOID_EDGE_NAMES[i]}`] = polygon.borders[i];
  }
  for (let i = 0; i < QUAD_ANGLE_KEYS.length; i++) {
    namedObjects[`angle.${QUAD_ANGLE_KEYS[i]}`] = angleElements[i];
  }
  namedObjects['point.A'] = polygon.vertices[0];
  namedObjects['point.B'] = polygon.vertices[1];
  namedObjects['point.C'] = polygon.vertices[2];
  namedObjects['point.D'] = polygon.vertices[3];

  return {
    jsxObjects,
    labels,
    ports,
    namedObjects,
    controlPoints: [pA, pB],
    vertexPoints: { A: pA, B: pB, C: pC, D: pD },
    destroy: () => {
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

function buildTrapezoidPorts(
  a: { X: () => number; Y: () => number },
  b: { X: () => number; Y: () => number },
  c: { X: () => number; Y: () => number },
  d: { X: () => number; Y: () => number },
): PortMap {
  return {
    'point.A': () => [a.X(), a.Y()] as PointPort,
    'point.B': () => [b.X(), b.Y()] as PointPort,
    'point.C': () => [c.X(), c.Y()] as PointPort,
    'point.D': () => [d.X(), d.Y()] as PointPort,

    'edge.bottom': () => ({ start: [a.X(), a.Y()], end: [b.X(), b.Y()] }) as EdgePort,
    'edge.right': () => ({ start: [b.X(), b.Y()], end: [c.X(), c.Y()] }) as EdgePort,
    'edge.top': () => ({ start: [c.X(), c.Y()], end: [d.X(), d.Y()] }) as EdgePort,
    'edge.left': () => ({ start: [d.X(), d.Y()], end: [a.X(), a.Y()] }) as EdgePort,

    'angleCenter.A': () => computeAngleCenter(a, d, b),
    'angleCenter.B': () => computeAngleCenter(b, a, c),
    'angleCenter.C': () => computeAngleCenter(c, b, d),
    'angleCenter.D': () => computeAngleCenter(d, c, a),

    // a = top parallel side (C to D)
    a: () => dist(c, d),
    // b = bottom parallel side (A to B)
    b: () => dist(a, b),
    // h = perpendicular distance from D to base line A-B
    h: () => {
      const bx = b.X() - a.X();
      const by = b.Y() - a.Y();
      const bLen = Math.sqrt(bx * bx + by * by);
      if (bLen === 0) {
        return 0;
      }
      const px = d.X() - a.X();
      const py = d.Y() - a.Y();
      return Math.abs(bx * py - by * px) / bLen;
    },
    // angle = base angle at A between edges A-B and A-D
    angle: () => {
      const dx1 = b.X() - a.X();
      const dy1 = b.Y() - a.Y();
      const dx2 = d.X() - a.X();
      const dy2 = d.Y() - a.Y();
      const dot = dx1 * dx2 + dy1 * dy2;
      const cross = dx1 * dy2 - dy1 * dx2;
      return (Math.abs(Math.atan2(cross, dot)) * 180) / Math.PI;
    },
    // s1 = left non-parallel side (A to D)
    s1: () => dist(a, d),
    // s2 = right non-parallel side (B to C)
    s2: () => dist(b, c),
    // d1 = diagonal A to C
    d1: () => dist(a, c),
    // d2 = diagonal B to D
    d2: () => dist(b, d),
    // area = (a + b) / 2 * h
    area: () => {
      const aLen = dist(c, d);
      const bLen = dist(a, b);
      const bx = b.X() - a.X();
      const by = b.Y() - a.Y();
      const baseLen = Math.sqrt(bx * bx + by * by);
      if (baseLen === 0) {
        return 0;
      }
      const px = d.X() - a.X();
      const py = d.Y() - a.Y();
      const h = Math.abs(bx * py - by * px) / baseLen;
      return ((aLen + bLen) / 2) * h;
    },
    // perimeter = a + b + s1 + s2
    perimeter: () => dist(c, d) + dist(a, b) + dist(a, d) + dist(b, c),
  };
}

function dist(a: { X: () => number; Y: () => number }, b: { X: () => number; Y: () => number }): number {
  const dx = b.X() - a.X();
  const dy = b.Y() - a.Y();
  return Math.sqrt(dx * dx + dy * dy);
}
